import { useLayoutEffect, useRef, useState } from 'react';
import { designTokens } from '../tokens';

/** 버튼만 측정하며 실제 입력·업무 컴포넌트는 복제하지 않습니다. */
export function useHeaderActionOverflow(enabled: boolean, hasPrimary: boolean, hasTools: boolean) {
  const headerRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);

  useLayoutEffect(() => {
    const header = headerRef.current;
    const measurement = measurementRef.current;
    if (!enabled || !header || !measurement) return;
    const measure = () => {
      const style = getComputedStyle(header);
      const width = header.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const gap = designTokens.space.xs;
      const fixedSelectors = ['page-header-primary-action', 'page-header-utilities', 'page-header-edit-actions'];
      const fixedWidth = fixedSelectors.reduce((sum, id) => {
        const element = header.querySelector<HTMLElement>(`[data-testid="${id}"]`);
        return sum + (element ? element.getBoundingClientRect().width + gap : 0);
      }, 0);
      // 제목과 탐색 도구가 버튼 때문에 계속 줄어드는 피드백을 피하고 각 영역의 최소 공간을 확보합니다.
      const contentReserve = designTokens.layout.pageHeaderTitleBasis * (hasTools ? 2 : 1);
      const available = Math.max(0, width - contentReserve - fixedWidth - gap * 2);
      const widths = Array.from(measurement.children, child => child.getBoundingClientRect().width);
      const maxVisible = Math.min(widths.length, hasPrimary ? 2 : 3);
      let next = 0;
      let used = 0;
      for (let index = 0; index < maxVisible; index += 1) {
        const candidate = used + widths[index] + (index > 0 ? gap : 0);
        const menuSpace = index < widths.length - 1 ? designTokens.layout.touchTargetMinSize + gap : 0;
        if (candidate + menuSpace > available) break;
        used = candidate;
        next += 1;
      }
      setVisibleCount(current => current === next ? current : next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    observer.observe(measurement);
    header.querySelectorAll<HTMLElement>('[data-testid="page-header-primary-action"], [data-testid="page-header-utilities"], [data-testid="page-header-edit-actions"]').forEach(element => observer.observe(element));
    return () => observer.disconnect();
  });

  return { headerRef, measurementRef, visibleCount };
}
